import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { Clock, Save, Loader2, Copy, Check, Globe, AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface TimeSlot {
  open: string;
  close: string;
}

/** One mode per day: either single slot or split (lunch + dinner), never both. */
type DayHoursSingle = {
  day: string;
  isOpen: boolean;
  mode: 'single';
  singleSlot: TimeSlot;
};

type DayHoursSplit = {
  day: string;
  isOpen: boolean;
  mode: 'split';
  lunchSlot: TimeSlot;
  dinnerSlot: TimeSlot;
};

type DayHours = DayHoursSingle | DayHoursSplit;

const DEFAULT_SINGLE: TimeSlot = { open: '09:00', close: '18:00' };
const DEFAULT_LUNCH: TimeSlot = { open: '09:00', close: '13:00' };
const DEFAULT_DINNER: TimeSlot = { open: '15:00', close: '21:00' };

function dayHoursSingle(day: string, isOpen: boolean, singleSlot?: TimeSlot): DayHoursSingle {
  return { day, isOpen, mode: 'single', singleSlot: singleSlot ?? DEFAULT_SINGLE };
}

function dayHoursSplit(day: string, isOpen: boolean, lunchSlot?: TimeSlot, dinnerSlot?: TimeSlot): DayHoursSplit {
  return {
    day,
    isOpen,
    mode: 'split',
    lunchSlot: lunchSlot ?? DEFAULT_LUNCH,
    dinnerSlot: dinnerSlot ?? DEFAULT_DINNER
  };
}

interface OpeningHoursEditorProps {
  menuId?: string | number;
  restaurantId?: string | number;
}

const DAYS = [
  { key: 'monday', label: 'opening_hours_editor.days.monday' },
  { key: 'tuesday', label: 'opening_hours_editor.days.tuesday' },
  { key: 'wednesday', label: 'opening_hours_editor.days.wednesday' },
  { key: 'thursday', label: 'opening_hours_editor.days.thursday' },
  { key: 'friday', label: 'opening_hours_editor.days.friday' },
  { key: 'saturday', label: 'opening_hours_editor.days.saturday' },
  { key: 'sunday', label: 'opening_hours_editor.days.sunday' }
];

const OpeningHoursEditor: React.FC<OpeningHoursEditorProps> = ({ menuId, restaurantId }) => {
  const { t } = useTranslation();
  const [openingHours, setOpeningHours] = useState<DayHours[]>(DAYS.map(day => dayHoursSingle(day.key, true)));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [showGoogleSection, setShowGoogleSection] = useState(true);

  useEffect(() => {
    if (menuId || restaurantId) {
      fetchOpeningHours();
      checkGoogleConnection();
    }
  }, [menuId, restaurantId]);

  const fetchOpeningHours = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      const response = await axios.get(`/restaurants/${id}/opening-hours`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const hours = response.data.data;
        setOpeningHours(DAYS.map(day => {
          const dayHours = hours[day.key];
          if (!dayHours || !dayHours.isOpen) {
            return dayHoursSingle(day.key, false);
          }
          if (dayHours.lunchSlot && dayHours.dinnerSlot) {
            return dayHoursSplit(day.key, true, dayHours.lunchSlot, dayHours.dinnerSlot);
          }
          return dayHoursSingle(day.key, true, dayHours.singleSlot);
        }));
      }
    } catch (error: any) {
      console.error('Failed to fetch opening hours:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkGoogleConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      const response = await axios.get(`/restaurants/${id}/google-connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setGoogleConnected(response.data.connected || false);
      }
    } catch (error) {
      console.error('Failed to check Google connection:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      const hoursData: any = {};

      openingHours.forEach(day => {
        if (day.isOpen) {
          if (day.mode === 'split') {
            hoursData[day.day] = {
              isOpen: true,
              singleSlot: null,
              lunchSlot: day.lunchSlot,
              dinnerSlot: day.dinnerSlot
            };
          } else {
            hoursData[day.day] = {
              isOpen: true,
              singleSlot: day.singleSlot,
              lunchSlot: null,
              dinnerSlot: null
            };
          }
        } else {
          hoursData[day.day] = { isOpen: false, singleSlot: null, lunchSlot: null, dinnerSlot: null };
        }
      });

      const response = await axios.put(
        `/restaurants/${id}/opening-hours`,
        { openingHours: hoursData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(t('opening_hours_editor.messages.save_success'));
        
        // Sync with Google if connected
        if (googleConnected) {
          syncWithGoogle();
        }

        // Generate embed code
        generateEmbedCode();
      }
    } catch (error: any) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.error || t('opening_hours_editor.messages.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const syncWithGoogle = async () => {
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      await axios.post(
        `/restaurants/${id}/sync-google-hours`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('opening_hours_editor.messages.google_sync_success'));
    } catch (error: any) {
      console.error('Google sync error:', error);
      toast.error(t('opening_hours_editor.messages.google_sync_error'));
    }
  };

  const importFromGoogle = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      const response = await axios.post(
        `/restaurants/${id}/import-google-hours`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchOpeningHours();
        toast.success(t('opening_hours_editor.messages.google_import_success'));
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.error || t('opening_hours_editor.messages.google_import_error'));
    } finally {
      setLoading(false);
    }
  };

  const connectGoogle = async () => {
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      const response = await axios.post(
        `/restaurants/${id}/connect-google`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setGoogleConnected(true);
        toast.success(t('opening_hours_editor.messages.google_connect_success'));
        await importFromGoogle();
      }
    } catch (error: any) {
      console.error('Connection error:', error);
      toast.error(error.response?.data?.error || t('opening_hours_editor.messages.google_connect_error'));
    }
  };

  const generateEmbedCode = () => {
    const baseUrl = window.location.origin;
    const id = restaurantId || menuId;
    const code = `<iframe src="${baseUrl}/opening-hours/${id}" width="100%" height="300" frameborder="0" style="border: 1px solid #ddd; border-radius: 8px;"></iframe>`;
    setEmbedCode(code);
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t('opening_hours_editor.messages.embed_copy_success'));
  };

  const updateDayHours = (dayKey: string, updates: Partial<DayHours>) => {
    setOpeningHours(openingHours.map(day => {
      if (day.day !== dayKey) return day;
      // When switching mode, set only that mode's shape (single OR split), never both
      if ('mode' in updates && updates.mode !== undefined) {
        const isOpen = updates.isOpen ?? day.isOpen;
        const u = updates as Partial<DayHoursSingle> & Partial<DayHoursSplit>;
        if (updates.mode === 'single') {
          const singleSlot = u.singleSlot ?? (day.mode === 'single' ? day.singleSlot : undefined);
          return dayHoursSingle(dayKey, isOpen, singleSlot);
        }
        const lunchSlot = u.lunchSlot ?? (day.mode === 'split' ? day.lunchSlot : undefined);
        const dinnerSlot = u.dinnerSlot ?? (day.mode === 'split' ? day.dinnerSlot : undefined);
        return dayHoursSplit(dayKey, isOpen, lunchSlot, dinnerSlot);
      }
      // Update only the current mode's fields
      if (day.mode === 'single') {
        return { ...day, ...updates } as DayHoursSingle;
      }
      return { ...day, ...updates } as DayHoursSplit;
    }));
  };

  const toggleDayExpansion = (dayKey: string) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(dayKey)) {
      newExpanded.delete(dayKey);
    } else {
      newExpanded.add(dayKey);
    }
    setExpandedDays(newExpanded);
  };

  const formatShortDayLabel = (labelKey: string) => {
    return t(labelKey).substring(0, 3);
  };

  const isDayExpanded = (dayKey: string) => {
    return expandedDays.has(dayKey);
  };

  if (loading && openingHours.length === 0) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl font-bold text-[#0A0C0B] mb-1 md:mb-2">{t('opening_hours_editor.title')}</h2>
          <p className="text-sm md:text-base text-[#687d76]">{t('opening_hours_editor.subtitle')}</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2.5 md:px-6 md:py-2 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full md:w-auto min-w-[140px]"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
              <span className="text-sm md:text-base">{t('opening_hours_editor.buttons.saving')}</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4 md:w-5 md:h-5" />
              <span className="text-sm md:text-base">{t('opening_hours_editor.buttons.save_hours')}</span>
            </>
          )}
        </button>
      </div>

      {/* Google My Business Integration - Collapsible on Mobile */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <button
          onClick={() => setShowGoogleSection(!showGoogleSection)}
          className="w-full p-4 md:p-6 flex items-center justify-between md:hidden"
        >
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#0A0C0B]" />
            <h3 className="text-base font-semibold text-[#0A0C0B]">{t('opening_hours_editor.google_integration.title')}</h3>
          </div>
          {showGoogleSection ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        
        {/* <div className={`${!showGoogleSection ? 'hidden md:block' : ''}`}>
          
          
          <div className="p-4 md:p-6 pt-4 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="md:hidden">
                <h4 className="text-sm font-medium text-[#687d76] mb-1">Status</h4>
                {googleConnected ? (
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                    {t('opening_hours_editor.google_integration.status.connected')}
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium">
                    {t('opening_hours_editor.google_integration.status.not_connected')}
                  </span>
                )}
              </div>
              
              {googleConnected ? (
                <div className="flex flex-col md:flex-row md:items-center gap-3 w-full">
                  <div className="hidden md:block">
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                      {t('opening_hours_editor.google_integration.status.connected_short')}
                    </span>
                  </div>
                  <button
                    onClick={importFromGoogle}
                    disabled={loading}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 w-full md:w-auto"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    <span className="text-sm md:text-base">{t('opening_hours_editor.google_integration.actions.import_from_google')}</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={connectGoogle}
                  className="px-4 py-2 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] flex items-center justify-center gap-2 w-full md:w-auto"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm md:text-base">{t('opening_hours_editor.google_integration.actions.connect_google')}</span>
                </button>
              )}
            </div>
          </div>
        </div> */}
      </div>

      {/* Opening Hours Editor - Mobile Optimized */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-[#0A0C0B] mb-4">{t('opening_hours_editor.weekly_schedule')}</h3>
        
        {/* Mobile: Days Grid */}
        <div className="md:hidden mb-4">
          <div className="grid grid-cols-2 gap-2">
            {openingHours.map((dayHours) => {
              const dayLabelKey = DAYS.find(d => d.key === dayHours.day)?.label || dayHours.day;
              const isExpanded = isDayExpanded(dayHours.day);
              
              return (
                <div key={dayHours.day}>
                  <button
                    onClick={() => toggleDayExpansion(dayHours.day)}
                    className={`w-full p-3 rounded-lg border flex items-center justify-between ${
                      isExpanded 
                        ? 'border-[#0A0C0B] bg-[#0A0C0B] text-white' 
                        : dayHours.isOpen 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-medium text-sm">{formatShortDayLabel(dayLabelKey)}</span>
                      {dayHours.isOpen && dayHours.mode === 'single' && dayHours.singleSlot && (
                        <span className="text-xs mt-1">
                          {dayHours.singleSlot.open.substring(0, 5)} - {dayHours.singleSlot.close.substring(0, 5)}
                        </span>
                      )}
                      {dayHours.isOpen && dayHours.mode === 'split' && (
                        <span className="text-xs mt-1">Split Hours</span>
                      )}
                      {!dayHours.isOpen && (
                        <span className="text-xs mt-1 text-red-600">Closed</span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {/* Expanded Day Details */}
                  {isExpanded && (
                    <div className="mt-2 p-3 border border-gray-200 rounded-lg bg-white">
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          checked={dayHours.isOpen}
                          onChange={(e) => updateDayHours(dayHours.day, { isOpen: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300 text-[#0A0C0B] focus:ring-[#7BD5B5]"
                        />
                        <label className="font-medium text-sm text-[#0A0C0B]">{t('opening_hours_editor.day_editor.open_this_day')}</label>
                      </div>
                      
                      {dayHours.isOpen && (
                        <>
                          <div className="flex gap-1 mb-4">
                            <button
                              onClick={() => updateDayHours(dayHours.day, { mode: 'single' })}
                              className={`px-3 py-1.5 text-xs rounded-lg flex-1 ${
                                dayHours.mode === 'single'
                                  ? 'bg-[#0A0C0B] text-white'
                                  : 'bg-gray-100 text-[#0A0C0B] hover:bg-gray-200'
                              }`}
                            >
                              {t('opening_hours_editor.day_editor.single')}
                            </button>
                            <button
                              onClick={() => updateDayHours(dayHours.day, { mode: 'split' })}
                              className={`px-3 py-1.5 text-xs rounded-lg flex-1 ${
                                dayHours.mode === 'split'
                                  ? 'bg-[#0A0C0B] text-white'
                                  : 'bg-gray-100 text-[#0A0C0B] hover:bg-gray-200'
                              }`}
                            >
                              {t('opening_hours_editor.day_editor.split')}
                            </button>
                          </div>
                          
                          {dayHours.mode === 'single' ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-[#687d76]" />
                                <span className="text-sm text-[#687d76]">{t('opening_hours_editor.day_editor.hours')}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">{t('opening_hours_editor.day_editor.open')}</label>
                                  <input
                                    type="time"
                                    value={dayHours.singleSlot?.open || '09:00'}
                                    onChange={(e) => updateDayHours(dayHours.day, {
                                      singleSlot: { ...dayHours.singleSlot!, open: e.target.value }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">{t('opening_hours_editor.day_editor.close')}</label>
                                  <input
                                    type="time"
                                    value={dayHours.singleSlot?.close || '18:00'}
                                    onChange={(e) => updateDayHours(dayHours.day, {
                                      singleSlot: { ...dayHours.singleSlot!, close: e.target.value }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-[#687d76] mb-2">{t('opening_hours_editor.day_editor.lunch')}</label>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Open</label>
                                    <input
                                      type="time"
                                      value={dayHours.lunchSlot?.open || '09:00'}
                                      onChange={(e) => updateDayHours(dayHours.day, {
                                        lunchSlot: { ...dayHours.lunchSlot!, open: e.target.value }
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Close</label>
                                    <input
                                      type="time"
                                      value={dayHours.lunchSlot?.close || '13:00'}
                                      onChange={(e) => updateDayHours(dayHours.day, {
                                        lunchSlot: { ...dayHours.lunchSlot!, close: e.target.value }
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-[#687d76] mb-2">{t('opening_hours_editor.day_editor.dinner')}</label>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Open</label>
                                    <input
                                      type="time"
                                      value={dayHours.dinnerSlot?.open || '15:00'}
                                      onChange={(e) => updateDayHours(dayHours.day, {
                                        dinnerSlot: { ...dayHours.dinnerSlot!, open: e.target.value }
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Close</label>
                                    <input
                                      type="time"
                                      value={dayHours.dinnerSlot?.close || '21:00'}
                                      onChange={(e) => updateDayHours(dayHours.day, {
                                        dinnerSlot: { ...dayHours.dinnerSlot!, close: e.target.value }
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop: Full List View */}
        <div className="hidden md:block space-y-4">
          {openingHours.map((dayHours) => {
            const dayLabelKey = DAYS.find(d => d.key === dayHours.day)?.label || dayHours.day;
            return (
              <div key={dayHours.day} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={dayHours.isOpen}
                      onChange={(e) => updateDayHours(dayHours.day, { isOpen: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-[#0A0C0B] focus:ring-[#7BD5B5]"
                    />
                    <label className="font-medium text-[#0A0C0B]">{t(dayLabelKey)}</label>
                  </div>
                  {dayHours.isOpen && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateDayHours(dayHours.day, { mode: 'single' })}
                        className={`px-3 py-1 text-sm rounded-lg ${
                          dayHours.mode === 'single'
                            ? 'bg-[#0A0C0B] text-white'
                            : 'bg-gray-100 text-[#0A0C0B] hover:bg-gray-200'
                        }`}
                      >
                        {t('opening_hours_editor.day_editor.single_slot')}
                      </button>
                      <button
                        onClick={() => updateDayHours(dayHours.day, { mode: 'split' })}
                        className={`px-3 py-1 text-sm rounded-lg ${
                          dayHours.mode === 'split'
                            ? 'bg-[#0A0C0B] text-white'
                            : 'bg-gray-100 text-[#0A0C0B] hover:bg-gray-200'
                        }`}
                      >
                        {t('opening_hours_editor.day_editor.split_lunch_dinner')}
                      </button>
                    </div>
                  )}
                </div>

                {dayHours.isOpen && (
                  <div className="ml-8 space-y-3">
                    {dayHours.mode === 'single' ? (
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-[#687d76]" />
                        <input
                          type="time"
                          value={dayHours.singleSlot?.open || '09:00'}
                          onChange={(e) => updateDayHours(dayHours.day, {
                            singleSlot: { ...dayHours.singleSlot!, open: e.target.value }
                          })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                        />
                        <span className="text-[#687d76]">to</span>
                        <input
                          type="time"
                          value={dayHours.singleSlot?.close || '18:00'}
                          onChange={(e) => updateDayHours(dayHours.day, {
                            singleSlot: { ...dayHours.singleSlot!, close: e.target.value }
                          })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-[#687d76] w-20">{t('opening_hours_editor.day_editor.lunch')}:</span>
                          <input
                            type="time"
                            value={dayHours.lunchSlot?.open || '09:00'}
                            onChange={(e) => updateDayHours(dayHours.day, {
                              lunchSlot: { ...dayHours.lunchSlot!, open: e.target.value }
                            })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                          />
                          <span className="text-[#687d76]">to</span>
                          <input
                            type="time"
                            value={dayHours.lunchSlot?.close || '13:00'}
                            onChange={(e) => updateDayHours(dayHours.day, {
                              lunchSlot: { ...dayHours.lunchSlot!, close: e.target.value }
                            })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-[#687d76] w-20">{t('opening_hours_editor.day_editor.dinner')}:</span>
                          <input
                            type="time"
                            value={dayHours.dinnerSlot?.open || '15:00'}
                            onChange={(e) => updateDayHours(dayHours.day, {
                              dinnerSlot: { ...dayHours.dinnerSlot!, open: e.target.value }
                            })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                          />
                          <span className="text-[#687d76]">to</span>
                          <input
                            type="time"
                            value={dayHours.dinnerSlot?.close || '21:00'}
                            onChange={(e) => updateDayHours(dayHours.day, {
                              dinnerSlot: { ...dayHours.dinnerSlot!, close: e.target.value }
                            })}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Embed Code - Mobile Optimized */}
      {embedCode && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-semibold text-[#0A0C0B]">{t('opening_hours_editor.embed_code.title')}</h3>
            <button
              onClick={copyEmbedCode}
              className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 w-full md:w-auto"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-sm md:text-base">{t('opening_hours_editor.buttons.copied')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="text-sm md:text-base">{t('opening_hours_editor.buttons.copy_code')}</span>
                </>
              )}
            </button>
          </div>
          <div className="relative">
            <textarea
              value={embedCode}
              readOnly
              className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 font-mono text-xs md:text-sm resize-none"
              rows={3}
              style={{ wordBreak: 'break-all' }}
            />
          </div>
          <p className="text-xs md:text-sm text-[#687d76] mt-2">
            {t('opening_hours_editor.embed_code.description')}
          </p>
        </div>
      )}

      {/* Info Box - Mobile Optimized */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1 text-sm md:text-base">{t('opening_hours_editor.info_box.title')}</h4>
            <p className="text-xs md:text-sm text-blue-800">
              {t('opening_hours_editor.info_box.description')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningHoursEditor;